import { DEFAULT_LANGUAGE } from '@datacamp/exercise-library-translation-utils';
import { url } from '@datacamp/exercise-library-utils';
import fetch from 'cross-fetch';
import { getEndpoints } from '../lib/endpoints';
import { AiFeatureFlagsSchema } from '../types/AIFeatureFlags';
import { HttpError } from './HttpError';
import { ValidationError } from './ValidationError';
const LANGUAGE_HEADER = 'x-dc-lang';
export class CampusApiClient {
  constructor(endpoint) {
    this.endpoint = endpoint;
  }
  async getIsRatingPermitted() {
    try {
      const result = await fetch(url(this.endpoint, 'api', 'rating', 'is_permitted'), {
        credentials: 'include',
        method: 'GET'
      });
      return result.json();
    } catch (error) {
      return false;
    }
  }
  rateExercise(exerciseId, payload) {
    return fetch(url(this.endpoint, 'api', 'exercises', exerciseId.toString(), 'submit_rating'), {
      body: JSON.stringify(payload),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    }).then(() => undefined);
  }
  async rateFeedback(ratingSettings) {
    const result = await fetch(url(this.endpoint, 'api', 'rating'), {
      body: JSON.stringify(ratingSettings),
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    });
    if (result.status >= 400) {
      throw new Error("Cannot rate this exercise id: ".concat(ratingSettings.exerciseId));
    }
    return result.json();
  }
  getSolution(exerciseId) {
    return fetch(url(this.endpoint, 'api', 'exercises', exerciseId.toString(), 'get_solution'), {
      credentials: 'include',
      method: 'GET'
    }).then(() => undefined);
  }
  getHint(exerciseId) {
    return fetch(url(this.endpoint, 'api', 'exercises', exerciseId.toString(), 'get_hint'), {
      credentials: 'include',
      method: 'GET'
    }).then(() => undefined);
  }
  async getExercise(exerciseId, language, validate) {
    const result = await fetch(url(this.endpoint, 'api', 'exercise', String(exerciseId)), {
      credentials: 'include',
      headers: {
        [LANGUAGE_HEADER]: language !== null && language !== void 0 ? language : DEFAULT_LANGUAGE,
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!result.ok) {
      const bodyText = await result.text();
      throw new HttpError({
        method: 'GET',
        status: result.status,
        text: bodyText,
        url: result.url
      });
    }
    const {
      data,
      type,
      version
    } = await result.json();
    if (validate === undefined) {
      return data;
    }
    try {
      return validate(type, version, data);
    } catch (error) {
      throw new ValidationError('Could not validate exercise.');
    }
  }
  async getCourse(courseId) {
    const result = await fetch(url(this.endpoint, 'api', 'courses', String(courseId)), {
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!result.ok) {
      const bodyText = await result.text();
      throw new HttpError({
        method: 'GET',
        status: result.status,
        text: bodyText,
        url: result.url
      });
    }
    const data = await result.json();
    return data;
  }
  async getExerciseProgress(exerciseId, language) {
    const result = await fetch(url(this.endpoint, 'api', 'exercises', String(exerciseId), 'progress'), {
      credentials: 'include',
      headers: {
        [LANGUAGE_HEADER]: language !== null && language !== void 0 ? language : DEFAULT_LANGUAGE,
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!result.ok) {
      const bodyText = await result.text();
      throw new HttpError({
        method: 'GET',
        status: result.status,
        text: bodyText,
        url: result.url
      });
    }
    const userHasNoProgress = result.status === 204;
    if (userHasNoProgress) {
      return undefined;
    }
    const data = await result.json();
    return data;
  }
  async getCourseAiFeaturesFlags(courseId) {
    const result = await fetch(url(this.endpoint, 'api', 'courses', String(courseId), 'ai_features'));
    if (!result.ok) {
      const bodyText = await result.text();
      throw new HttpError({
        method: 'GET',
        status: result.status,
        text: bodyText,
        url: result.url
      });
    }
    const data = await result.json();
    return AiFeatureFlagsSchema.parse(data);
  }
}
export const campusApiClient = environment => new CampusApiClient(getEndpoints(environment).CAMPUS_API);